/*
 * Pure rule-selection logic, ported from the rule helpers in taskpane.js.
 *
 * The originals read the global `state`; here every function takes the rules
 * array (and other inputs) explicitly so they stay pure and testable. The
 * display/preset helpers are added in Phase 3 alongside RulesPanel.
 */

import { getDict } from "../i18n/translations";
import type {
  BuiltInPreset,
  CatalogPreferences,
  CatalogSections,
  FriendlyRule,
  Lang,
  LocalizedDisplay,
  PreferenceValues,
  Rule,
} from "../state/types";

interface PreferenceConfig {
  defaultValue?: string;
  options?: string[];
}

export function getBatchRules(rules: Rule[]): Rule[] {
  return rules.filter((rule) => rule.section !== "confusion");
}

export function getConfusionRules(rules: Rule[]): Rule[] {
  return rules.filter((rule) => rule.section === "confusion");
}

export function getLiveRules(rules: Rule[]): Rule[] {
  return getBatchRules(rules);
}

/** Expand a set of rule ids to include their dependencies. */
export function resolveRuleSelection(rules: Rule[], ruleIds: string[]): string[] {
  const batchRules = getBatchRules(rules);
  const knownIds = new Set(batchRules.map((rule) => rule.id));
  const selected = new Set<string>();
  const visiting = new Set<string>();

  const addRule = (ruleId: string): void => {
    if (!knownIds.has(ruleId) || selected.has(ruleId) || visiting.has(ruleId)) return;
    visiting.add(ruleId);
    const rule = batchRules.find((item) => item.id === ruleId);
    (rule?.dependsOn || []).forEach(addRule);
    visiting.delete(ruleId);
    selected.add(ruleId);
  };

  (ruleIds || []).forEach(addRule);
  return [...selected];
}

export function selectRuleWithDependencies(
  rules: Rule[],
  selectedRuleIds: string[],
  ruleId: string
): string[] {
  return resolveRuleSelection(rules, [...selectedRuleIds, ruleId]);
}

export function deselectRuleAndDependents(
  rules: Rule[],
  selectedRuleIds: string[],
  ruleId: string
): string[] {
  const batchRules = getBatchRules(rules);
  const removed = new Set<string>([ruleId]);
  let changed = true;
  while (changed) {
    changed = false;
    batchRules.forEach((rule) => {
      if (removed.has(rule.id)) return;
      if ((rule.dependsOn || []).some((dependencyId) => removed.has(dependencyId))) {
        removed.add(rule.id);
        changed = true;
      }
    });
  }

  return selectedRuleIds.filter((id) => !removed.has(id));
}

export function buildDefaultPreferences(
  preferenceCatalog: CatalogPreferences = {}
): PreferenceValues {
  return Object.fromEntries(
    Object.entries(preferenceCatalog).map(([key, rawConfig]) => {
      const config = (rawConfig || {}) as PreferenceConfig;
      return [key, config.defaultValue || config.options?.[0] || ""];
    })
  );
}

export function getSelectedConfusionRuleIds(
  rules: Rule[],
  selectedConfusionRuleIds: string[]
): string[] {
  const knownConfusionIds = new Set(getConfusionRules(rules).map((rule) => rule.id));
  return selectedConfusionRuleIds.filter((ruleId) => knownConfusionIds.has(ruleId));
}

/* ─── Display helpers (localized copy from the backend catalog) ───────────── */

export function getLocalizedDisplay(
  display: Record<string, unknown> = {},
  lang: Lang
): LocalizedDisplay | null {
  return (display[lang] || display.en || display.el || null) as LocalizedDisplay | null;
}

export function humanizeRuleId(id: string, lang: Lang): string {
  const cleaned = String(id || "")
    .replace(/_normalize$/i, "")
    .replace(/_preference$/i, " preference")
    .replace(/_confusion$/i, " review")
    .replace(/_/g, " ")
    .trim();

  if (!cleaned) return lang === "el" ? "Κανόνας επιμέλειας" : "Editorial rule";
  return cleaned.charAt(0).toLocaleUpperCase(lang === "el" ? "el" : "en") + cleaned.slice(1);
}

export function getCatalogSectionDisplay(
  catalogSections: CatalogSections,
  sectionKey: string | undefined,
  lang: Lang
): { title: string; description: string } {
  const raw = (catalogSections?.[sectionKey ?? ""] as Record<string, unknown>) || {};
  const display = getLocalizedDisplay(raw, lang);
  return {
    title: display?.title || humanizeRuleId(sectionKey ?? "", lang),
    description: display?.description || getDict(lang).ruleFallbackDescription,
  };
}

export function getCatalogPreferenceDisplay(
  catalogPreferences: CatalogPreferences,
  preferenceKey: string,
  lang: Lang
): { label: string; options: Record<string, string> } {
  const config = catalogPreferences[preferenceKey] as
    | { display?: Record<string, unknown> }
    | undefined;
  const display = getLocalizedDisplay(config?.display || {}, lang);
  return {
    label: display?.label || humanizeRuleId(preferenceKey, lang),
    options: display?.options || {},
  };
}

export function getPreferenceOptions(
  catalogPreferences: CatalogPreferences,
  preferenceKey: string
): string[] {
  const config = catalogPreferences[preferenceKey] as PreferenceConfig | undefined;
  return Array.isArray(config?.options) ? config.options : [];
}

export function getFriendlyRule(
  ruleOrId: Rule | string,
  rules: Rule[],
  catalogSections: CatalogSections,
  lang: Lang
): FriendlyRule {
  const rule: Rule =
    typeof ruleOrId === "string"
      ? rules.find((item) => item.id === ruleOrId) || { id: ruleOrId, section: "" }
      : ruleOrId;
  const display = getLocalizedDisplay(rule.display || {}, lang);
  const section = getCatalogSectionDisplay(catalogSections, rule.section, lang);

  return {
    name: display?.title || humanizeRuleId(rule.id, lang),
    desc: display?.description || section.description || getDict(lang).ruleFallbackDescription,
    example: display?.example || "",
    cases: display?.cases || "",
  };
}

export function getBuiltInPresets(rules: Rule[], lang: Lang): BuiltInPreset[] {
  const dict = getDict(lang).rulePresets;
  const batchRules = getBatchRules(rules);
  const bySection = (section: string): string[] =>
    batchRules.filter((rule) => rule.isSafe && rule.section === section).map((rule) => rule.id);
  const byIds = (ids: string[]): string[] =>
    ids.filter((id) => batchRules.some((rule) => rule.id === id));
  const safeRuleIds = batchRules.filter((rule) => rule.isSafe).map((rule) => rule.id);

  const base: Array<{ id: keyof typeof dict; ruleIds: string[] }> = [
    { id: "conservative", ruleIds: resolveRuleSelection(rules, safeRuleIds) },
    { id: "orthography", ruleIds: resolveRuleSelection(rules, bySection("orthography")) },
    { id: "literary", ruleIds: resolveRuleSelection(rules, bySection("literary")) },
    {
      id: "dialogue",
      ruleIds: resolveRuleSelection(
        rules,
        byIds([
          "guillemets_normalize",
          "quote_comma_trim",
          "quote_period_preference",
          "ellipsis_normalize",
          "comma_space_normalize",
          "period_space_normalize",
        ])
      ),
    },
    { id: "authorVoice", ruleIds: resolveRuleSelection(rules, bySection("orthography")) },
  ];

  return base.map((preset) => ({
    id: preset.id,
    ruleIds: preset.ruleIds,
    title: dict[preset.id].title,
    description: dict[preset.id].description,
  }));
}

/* Mirrors the backend buildGreekEditorSelectionSignature. */
export function buildSelectionSignature(
  ruleIds: string[],
  preferences: PreferenceValues = {},
  protectedTerms: string[] = [],
  outputDefaults: { docxReviewMode?: string; includeReport?: boolean } = {}
): string {
  return JSON.stringify({
    ruleIds: [...new Set(ruleIds.map((id) => String(id || "").trim()).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b)
    ),
    preferences: Object.fromEntries(
      Object.entries(preferences)
        .filter(([, value]) => typeof value === "string" && value.length > 0)
        .sort(([a], [b]) => a.localeCompare(b))
    ),
    protectedTerms: [...new Set(protectedTerms.map((t) => t.trim()).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b)
    ),
    outputDefaults: {
      docxReviewMode: outputDefaults.docxReviewMode || "tracked",
      includeReport: outputDefaults.includeReport === true,
    },
  });
}

export function buildKnownPresetSignatures(
  rules: Rule[],
  lang: Lang,
  customPresets: Array<{
    ruleIds: string[];
    preferences?: PreferenceValues;
    protectedTerms?: string[];
    outputDefaults?: { docxReviewMode?: string; includeReport?: boolean };
  }>,
  preferences: PreferenceValues
): Set<string> {
  const signatures = new Set<string>();
  for (const preset of getBuiltInPresets(rules, lang)) {
    signatures.add(buildSelectionSignature(preset.ruleIds, preferences));
  }
  for (const cp of customPresets) {
    signatures.add(buildSelectionSignature(cp.ruleIds, cp.preferences || {}));
  }
  return signatures;
}
